// Mirrors @skinseoul/core CampaignOperations: the influencer team's master-sheet tracker fields on a campaign.

export enum CampaignOperationsKind {
  Standard = "standard",
  Paid = "paid",
}

export enum CampaignVisibility {
  Public = "PUBLIC",
  Internal = "INTERNAL",
}

export type CampaignOperationsPhase = "onboarding" | "brief" | "shipping" | "posting" | "report" | "government";

export const CAMPAIGN_OPERATIONS_PHASES: readonly CampaignOperationsPhase[] = ["onboarding", "brief", "shipping", "posting", "report", "government"];

export const CAMPAIGN_OPERATIONS_PHASE_LABELS: Record<CampaignOperationsPhase, string> = {
  onboarding: "Onboarding",
  brief: "Content Brief",
  shipping: "Shipping",
  posting: "Posting",
  report: "Report",
  government: "Gov. Report",
};

export const CAMPAIGN_OPERATIONS_PHASE_STATUSES: Record<CampaignOperationsPhase, readonly string[]> = {
  onboarding: ["Not Started", "In Progress", "Waiting: Brand Feedback", "Completed"],
  brief: ["Not Started", "In Progress", "Waiting: Brand Feedback", "Need Revision", "Paused", "Sent to Creator", "Completed"],
  shipping: ["Not Started", "WIP Shipping List", "Requested to FT/Brand", "Waiting: Tracking Number", "Tracking Number Sent", "Completed"],
  posting: ["Not Started", "On Track", "At Risk", "Off Track", "Completed"],
  report: ["Not Started", "In Progress", "Completed"],
  government: ["Not Needed", "Not Started", "In Progress", "Completed"],
};

export interface CampaignOperationsPics {
  brand?: string | null;
  scouting?: string | null;
  content?: string | null;
}

export interface CampaignOperationsLinks {
  notion?: string | null;
  creatorList?: string | null;
  contentTracker?: string | null;
  contract?: string | null;
  paymentTracker?: string | null;
}

export interface CampaignOperationsScouting {
  requirement?: string | null;
  note?: string | null;
}

export interface CampaignOperationsPhaseState {
  status?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  note?: string | null;
  link?: string | null;
}

export interface CampaignOperationsGovernmentState extends CampaignOperationsPhaseState {
  required?: boolean | null;
  requirement?: string | null;
}

export interface CampaignOperationsPhases {
  onboarding?: CampaignOperationsPhaseState | null;
  brief?: CampaignOperationsPhaseState | null;
  shipping?: CampaignOperationsPhaseState | null;
  posting?: CampaignOperationsPhaseState | null;
  report?: CampaignOperationsPhaseState | null;
  government?: CampaignOperationsGovernmentState | null;
}

export interface CampaignOperationsCancellation {
  at: string;
  reason: string;
}

export interface CampaignOperationsHistoryEntry {
  at: string;
  by: string | null;
  field: string;
  from: unknown;
  to: unknown;
}

/** A shipping and posting wave inside one campaign; creators join it through CampaignInfluencer.batchId. */
export interface CampaignOperationsBatch {
  name?: string | null;
  note?: string | null;
  phases?: CampaignOperationsPhases | null;
}

/** Batches keyed by a client-generated id; null deletes one. */
export type CampaignOperationsBatches = Record<string, CampaignOperationsBatch | null>;

/** Values the editor last saw, keyed by dotted operations path; any mismatch makes the server answer 409. */
export type CampaignOperationsExpectedValues = Record<string, unknown>;

/** One field the server reported as changed by someone else since the editor loaded it. */
export interface CampaignOperationsConflict {
  field: string;
  expected: unknown;
  current: unknown;
}

/** Patch shape: omitted keys stay, null clears. */
export interface CampaignOperationsFields {
  kind?: CampaignOperationsKind | null;
  pics?: CampaignOperationsPics | null;
  links?: CampaignOperationsLinks | null;
  scouting?: CampaignOperationsScouting | null;
  shippingMethod?: string | null;
  phases?: CampaignOperationsPhases | null;
  cancelled?: CampaignOperationsCancellation | null;
  note?: string | null;
  batches?: CampaignOperationsBatches | null;
}

export interface CampaignOperations extends CampaignOperationsFields {
  history?: CampaignOperationsHistoryEntry[];
}
